import { displayText } from "../../locales/display";
import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import { formatDisplayDateTime } from "src/utils/dateTime";
import { Badge } from "@ui/badge/Badge";
import { Box, HBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import { Error } from "@ui/error/Error";
import Input from "@ui/input/Input";
import Panel from "@ui/panel/Panel";
import { Select } from "@ui/select/Select";
import Spacer from "@ui/spacer/Spacer";
import { Typo } from "@ui/text/Typo";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import QRCode from "qrcode";
import { GD } from "src/GD";
import { AppLanguage, getAppLanguage } from "src/i18n";
import { IAuth2FASetupTotpResponse, IAuthSession, resolveTwoFactorStatus, TTwoFactorStatus } from "src/services/AuthService";
import { notify } from "../../tools/notify";
import { Avatar } from "../Avatar";
import { getRoleName } from "src/permissions";

const InfoRow = ({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) => { useLocale(); return ((
	<HBox
		sx={{
			padding: "10px 0",
			borderBottom: "1px solid #f0f0f0",
			alignItems: "center",
			"&:last-child": { borderBottom: "none" },
		}}>
		<Typo variant="small" sx={{ color: "#6b7280", width: "130px", flexShrink: 0 }}>
			{label}
		</Typo>
		<Box sx={{ flex: 1 }}>
			{typeof value === "string" ? (
				<Typo variant="medium" sx={mono ? { fontFamily: "monospace", fontSize: "12px" } : undefined}>
					{value}
				</Typo>
			) : (
				value
			)}
		</Box>
	</HBox>
)); };

const SectionCard = ({ title, children }: { title: string; children: React.ReactNode }) => { useLocale(); return ((
	<VBox
		sx={{
			backgroundColor: "#fff",
			borderRadius: "10px",
			padding: "16px 18px",
			boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
		}}>
		<Typo variant="medium" sx={{ color: "#374151", fontWeight: 600, marginBottom: "4px" }}>
			{title}
		</Typo>
		{children}
	</VBox>
)); };

const SecurityMethodCard = ({
	title,
	subtitle,
	highlight,
	selectedLabel,
	selected,
	onClick,
}: {
	title: string;
	subtitle: string;
	highlight: string;
	selectedLabel: string;
	selected: boolean;
	onClick: () => void;
}) => { useLocale(); return ((
	<Box
		onClick={onClick}
		sx={{
			flex: "1 1 210px",
			padding: "12px",
			borderRadius: "12px",
			border: selected ? "1px solid #2563eb" : "1px solid #e5e7eb",
			background: selected ? "linear-gradient(180deg, #eff6ff 0%, #ffffff 100%)" : "#ffffff",
			boxShadow: selected ? "0 8px 18px rgba(37, 99, 235, 0.1)" : "0 1px 6px rgba(15, 23, 42, 0.04)",
			cursor: "pointer",
			transition: "all 0.18s ease",
			"&:hover": {
				borderColor: "#93c5fd",
				transform: "translateY(-1px)",
				boxShadow: "0 10px 20px rgba(15, 23, 42, 0.07)",
			},
		}}>
		<VBox gap="8px">
			<HBox gap="8px" sx={{ justifyContent: "space-between", alignItems: "flex-start" }}>
				<Box
					sx={{
						width: "40px",
						height: "40px",
						borderRadius: "8px",
						background: selected ? "rgba(37, 99, 235, 0.12)" : "#f8fafc",
						color: selected ? "#2563eb" : "#475569",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						fontSize: "11px",
						fontWeight: 700,
						letterSpacing: "0.04em",
						textTransform: "uppercase",
					}}>
					{(title === "Email OTP" || title === msg("emailOtp")) ? "OTP" : "TOTP"}
				</Box>
				{selected ? <Badge label={selectedLabel} /> : null}
			</HBox>
			<VBox gap="3px">
				<Typo variant="medium" sx={{ color: "#0f172a", fontWeight: 600 }}>
					{title}
				</Typo>
				<Typo variant="small" sx={{ color: "#64748b", lineHeight: "17px", fontSize: "12px" }}>
					{subtitle}
				</Typo>
			</VBox>
			<Box
				sx={{
					padding: "7px 9px",
					borderRadius: "8px",
					background: selected ? "rgba(37, 99, 235, 0.08)" : "#f8fafc",
					border: "1px solid rgba(148, 163, 184, 0.18)",
				}}>
				<Typo variant="small" sx={{ color: selected ? "#1d4ed8" : "#475569", lineHeight: "16px", fontSize: "10px" }}>
					{highlight}
				</Typo>
			</Box>
		</VBox>
	</Box>
)); };

const formatDate = (dateStr: string | null | undefined, locale: string): string => formatDisplayDateTime(dateStr, { locale, date: { day: "2-digit", month: "short", year: "numeric" }, time: { hour: "2-digit", minute: "2-digit" } });

const showSuccessToast = (message: string) => {
	notify.success(message);
};

const getSessionStatusColor = (status: string, current: boolean): string => {
	if (current) return "#2563eb";
	if (status.toLowerCase() === "active") return "#16a34a";
	return "#64748b";
};

const isActiveSession = (session: IAuthSession): boolean => session.status.toLowerCase() === "active";

const isTerminableSession = (session: IAuthSession): boolean => !session.current && isActiveSession(session);

const getProjectName = (project: unknown): string | null => {
	if (!project) return null;
	if (typeof project === "string") return project;
	if (typeof project === "object" && "name" in project && typeof project.name === "string") {
		return project.name;
	}

	return null;
};

const TWO_FACTOR_METHODS = ["Email OTP", "Google Authenticator"] as const;
type TTwoFactorMethod = (typeof TWO_FACTOR_METHODS)[number];

export const ProfilePopup = () => {
	useLocale();
	const { t, i18n } = useTranslation();
	const request = GD.S_PROFILE_POPUP.use();
	const [user] = GD.REQ_PROFILE.useRequest();
	const [project] = GD.REQ_CURRENT_PROJECT.useRequest();

	const [name, setName] = useState({ value: "" });
	const [currentPassword, setCurrentPassword] = useState({ value: "" });
	const [newPassword, setNewPassword] = useState({ value: "" });
	const [confirmPassword, setConfirmPassword] = useState({ value: "" });
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<IError>();
	const [isEditing, setIsEditing] = useState(false);
	const [totpSetup, setTotpSetup] = useState<IAuth2FASetupTotpResponse | null>(null);
	const [totpCode, setTotpCode] = useState({ value: "" });
	const [totpQrCode, setTotpQrCode] = useState("");
	const [twoFactorStatus, setTwoFactorStatus] = useState<TTwoFactorStatus>("disabled");
	const twoFactorStatusRef = useRef<TTwoFactorStatus | null>(null);
	const [selectedTwoFactorMethod, setSelectedTwoFactorMethod] = useState<TTwoFactorMethod>(TWO_FACTOR_METHODS[0]);
	const [sessions, setSessions] = useState<IAuthSession[]>([]);
	const [sessionsBusy, setSessionsBusy] = useState(false);
	const language = getAppLanguage();
	const locale = language === "ru" ? "ru-RU" : "en-GB";
	const languageOptions = [
		{ name: t("profile.languages.en"), value: "en" as AppLanguage },
		{ name: t("profile.languages.ru"), value: "ru" as AppLanguage },
	];

	useEffect(() => {
		if (request && user) {
			const nextTwoFactorStatus = twoFactorStatusRef.current ?? resolveTwoFactorStatus(user.status_2fa, "disabled") ?? "disabled";

			setName({ value: user.name });
			setCurrentPassword({ value: "" });
			setNewPassword({ value: "" });
			setConfirmPassword({ value: "" });
			setTotpSetup(null);
			setTotpCode({ value: "" });
			setTotpQrCode("");
			setTwoFactorStatus(nextTwoFactorStatus);
			setSelectedTwoFactorMethod(TWO_FACTOR_METHODS[0]);
			setIsEditing(false);
			setError(undefined);
		}
	}, [request, user]);

	useEffect(() => {
		if (!request) return;

		let cancelled = false;

		const loadSessions = async () => {
			setSessionsBusy(true);
			const response = await GD.REQ_AUTH_SESSIONS.request();

			if (cancelled) {
				return;
			}

			setSessionsBusy(false);

			if (response.error) {
				setError(response.error);
				return;
			}

			setSessions(response.data ?? []);
		};

		loadSessions();

		return () => {
			cancelled = true;
		};
	}, [request]);

	useEffect(() => {
		let cancelled = false;

		if (!totpSetup?.url) {
			setTotpQrCode("");
			return;
		}

		if (/^(https?:\/\/|data:image\/)/i.test(totpSetup.url)) {
			setTotpQrCode(totpSetup.url);
			return;
		}

		QRCode.toDataURL(totpSetup.url, {
			margin: 1,
			width: 220,
		})
			.then((url: string) => {
				if (!cancelled) {
					setTotpQrCode(url);
				}
			})
			.catch(() => {
				if (!cancelled) {
					setTotpQrCode("");
				}
			});

		return () => {
			cancelled = true;
		};
	}, [totpSetup]);

	if (!request || !user || !project) return null;

	const closePopup = () => {
		setName({ value: "" });
		setCurrentPassword({ value: "" });
		setNewPassword({ value: "" });
		setConfirmPassword({ value: "" });
		setError(undefined);
		setIsEditing(false);
		GD.S_PROFILE_POPUP.invoke(null);
	};

	const handleSave = async () => {
		setError(undefined);

		if (newPassword.value && newPassword.value !== confirmPassword.value) {
			setError({ code: 400, message: t("profile.passwordsMismatch") });
			return;
		}

		setBusy(true);
		const req = await GD.REQ_API.request({
			method: "users/profile/update",
			data: {
				name: name.value,
				current_password: currentPassword.value || undefined,
				new_password: newPassword.value || undefined,
			},
		});
		setBusy(false);

		if (req.error) {
			setError(req.error);
		} else {
			GD.S_PROFILE_PATCH.invoke({ name: name.value });
			showSuccessToast(t("profile.profileUpdated"));
			closePopup();
		}
	};

	const handleEnableEmailTwoFactor = async () => {
		setError(undefined);
		setBusy(true);
		const response = await GD.REQ_AUTH_2FA_SETUP_EMAIL.request();
		setBusy(false);

		if (response.error) {
			setError(response.error);
			return;
		}

		const nextTwoFactorStatus = resolveTwoFactorStatus(response.data, "activated");
		const projectName = getProjectName(response.data.project);
		setTotpSetup(null);
		setTotpCode({ value: "" });
		if (nextTwoFactorStatus) {
			twoFactorStatusRef.current = nextTwoFactorStatus;
			setTwoFactorStatus(nextTwoFactorStatus);
			GD.S_PROFILE_PATCH.invoke({ status_2fa: nextTwoFactorStatus });
		}
		showSuccessToast(projectName ? t("profile.emailOtpEnabledProject", { project: projectName }) : t("profile.emailOtpEnabled"));
	};

	const handleSetupTotp = async () => {
		setError(undefined);
		setBusy(true);
		const response = await GD.REQ_AUTH_2FA_SETUP_TOTP.request();
		setBusy(false);

		if (response.error) {
			setError(response.error);
			return;
		}

		const nextTwoFactorStatus = resolveTwoFactorStatus(response.data, twoFactorStatus);
		if (nextTwoFactorStatus) {
			setTwoFactorStatus(nextTwoFactorStatus);
		}
		setTotpSetup(response.data);
		setTotpCode({ value: "" });
	};

	const handleStartTwoFactorSetup = async () => {
		if (selectedTwoFactorMethod === "Google Authenticator") {
			await handleSetupTotp();
			return;
		}

		await handleEnableEmailTwoFactor();
	};

	const handleConfirmTotp = async () => {
		if (!totpSetup?.pending_token) {
			setError({ code: -1, message: msg("totpSetupSessionExpiredStartTheSetupAgain") });
			return;
		}

		setError(undefined);
		setBusy(true);
		const response = await GD.REQ_AUTH_2FA_CONFIRM_TOTP.request({
			pending_token: totpSetup.pending_token,
			code: totpCode.value,
		});
		setBusy(false);

		if (response.error) {
			setError(response.error);
			return;
		}

		const nextTwoFactorStatus = resolveTwoFactorStatus(response.data, "activated");
		setTotpSetup(null);
		setTotpCode({ value: "" });
		if (nextTwoFactorStatus) {
			twoFactorStatusRef.current = nextTwoFactorStatus;
			setTwoFactorStatus(nextTwoFactorStatus);
			GD.S_PROFILE_PATCH.invoke({ status_2fa: nextTwoFactorStatus });
		}
		showSuccessToast(t("profile.totpEnabled"));
	};

	const handleDisableTwoFactor = async () => {
		setError(undefined);
		setBusy(true);
		const response = await GD.REQ_AUTH_2FA_DISABLE.request();
		setBusy(false);

		if (response.error) {
			setError(response.error);
			return;
		}

		const nextTwoFactorStatus = resolveTwoFactorStatus(response.data, "disabled");
		setTotpSetup(null);
		setTotpCode({ value: "" });
		if (nextTwoFactorStatus) {
			twoFactorStatusRef.current = nextTwoFactorStatus;
			setTwoFactorStatus(nextTwoFactorStatus);
			GD.S_PROFILE_PATCH.invoke({ status_2fa: nextTwoFactorStatus });
		}
		showSuccessToast(t("profile.twoFactorDisabled"));
	};

	const handleCopyTotpUrl = async () => {
		if (!totpSetup?.url) return;

		try {
			await navigator.clipboard.writeText(totpSetup.url);
			showSuccessToast(t("profile.setupLinkCopied"));
		} catch {
			setError({ code: -1, message: t("profile.copySetupLinkError") });
		}
	};

	const resetTotpSetup = () => {
		setTotpSetup(null);
		setTotpCode({ value: "" });
		setTotpQrCode("");
	};

	const handleLogoutOtherSessions = async () => {
		setError(undefined);
		setSessionsBusy(true);

		const response = await GD.REQ_AUTH_LOGOUT_OTHERS.request();
		if (response.error) {
			setSessionsBusy(false);
			setError(response.error);
			return;
		}

		showSuccessToast(t("profile.otherSessionsTerminated"));

		const sessionsResponse = await GD.REQ_AUTH_SESSIONS.request();
		setSessionsBusy(false);

		if (sessionsResponse.error) {
			setError(sessionsResponse.error);
			return;
		}

		setSessions(sessionsResponse.data ?? []);
	};

	const currentProject = user.projects.find(val => val.id === project.id);
	const userRoles = currentProject?.roles.map(val => val.name ?? getRoleName(val) ?? t("profile.role", { id: val.id ?? val.role_id ?? "unknown" })) ?? [];
	const twoFactorMethodDescription =
		selectedTwoFactorMethod === "Google Authenticator"
			? t("profile.totpDescription")
			: t("profile.emailOtpDescription");
	const isTwoFactorActive = twoFactorStatus === "activated";
	const twoFactorStatusMessage = isTwoFactorActive
		? t("profile.protectionEnabledHint")
		: t("profile.protectionDisabledHint");
	const otherSessionsCount = sessions.filter(isTerminableSession).length;

	return (
		<Panel
			busy={busy}
			contentPadding="0px 20px 20px 20px"
			sx={{
				width: "90vw",
				maxWidth: "700px",
				padding: "0",
				height: "80vh",
				maxHeight: "90vh",
				backgroundColor: "#f3f4f6",
			}}
			decorator="absolute"
			label={t("profile.title")}
			onClose={closePopup}>
			<Spacer size="16px" />

			{error && (
				<>
					<Error error={error} />
					<Spacer size="12px" />
				</>
			)}

			{!isEditing ? (
				<VBox gap="14px">
					{/* Header card */}
					<HBox
						center
						gap="16px"
						sx={{
							backgroundColor: "#fff",
							borderRadius: "10px",
							padding: "20px",
							boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
						}}>
						<Avatar size="56px" />
						<VBox gap="4px" sx={{ flex: 1 }}>
							<Typo variant="head">{user.name}</Typo>
							<Typo variant="small" sx={{ color: "#6b7280" }}>
								{user.email}
							</Typo>
							<Spacer size="6px" />
							<HBox gap="6px" sx={{ flexWrap: "wrap" }}>
								{userRoles.map(role => (
									<Badge key={role} label={role} />
								))}
								<Badge label={user.status} />
							</HBox>
						</VBox>
					</HBox>

					<SectionCard title={t("profile.preferences")}>
						<HBox gap="16px" sx={{ paddingTop: "10px", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
							<VBox gap="3px" sx={{ flex: "1 1 240px" }}>
								<Typo variant="medium">{t("profile.language")}</Typo>
								<Typo variant="small" sx={{ color: "#6b7280" }}>{t("profile.languageHint")}</Typo>
							</VBox>
							<Select
								size="small"
								sx={{ minWidth: "180px" }}
								value={languageOptions.find(option => option.value === language) ?? languageOptions[0]}
								values={languageOptions}
								onChange={value => void i18n.changeLanguage(value as unknown as AppLanguage)}
							/>
						</HBox>
					</SectionCard>

					{/* Account info */}
					<SectionCard title={t("profile.account")}>
						<InfoRow label={t("profile.userId")} value={String(user.id)} mono />
						<InfoRow label={msg("email_969ccbd3")} value={user.email} />
						<InfoRow
							label={t("profile.emailStatus")}
							value={
								<Box sx={{ flex: 0, display: "flex" }}>
									<Badge label={user.status_email} />
								</Box>
							}
						/>
						<InfoRow label={t("profile.phone")} value={user.phone ?? t("common.notSet")} />
						<InfoRow label={t("profile.type")} value={user.type} />
						<InfoRow label={t("profile.branch")} value={user.branch} />
						<InfoRow label={t("profile.signInAttempts")} value={user.signin_attempts !== null ? String(user.signin_attempts) : "0"} />
					</SectionCard>

					<SectionCard title={t("profile.security")}>
						<Box
							sx={{
								marginTop: "14px",
								padding: "12px 14px",
								borderRadius: "12px",
								background: isTwoFactorActive
									? "linear-gradient(180deg, #ecfdf5 0%, #ffffff 100%)"
									: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)",
								border: isTwoFactorActive ? "1px solid rgba(16, 185, 129, 0.18)" : "1px solid rgba(148, 163, 184, 0.18)",
							}}>
							<HBox gap="10px" sx={{ justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap" }}>
								<VBox gap="4px" sx={{ flex: 1, minWidth: "220px" }}>
									<Typo variant="medium" sx={{ color: "#0f172a", fontWeight: 600 }}>
										{t("profile.twoFactorProtection")}
									</Typo>
									<Typo variant="small" sx={{ color: "#64748b", lineHeight: "17px", fontSize: "12px" }}>
										{twoFactorStatusMessage}
									</Typo>
								</VBox>
								<Box sx={{ flex: 0, display: "flex" }}>
									<Badge label={twoFactorStatus} />
								</Box>
							</HBox>
						</Box>
						<Spacer size="10px" />

						<HBox gap="10px" sx={{ flexWrap: "wrap" }}>
							{isTwoFactorActive ? (
								<Box
									sx={{
										width: "100%",
										padding: "12px 14px",
										borderRadius: "10px",
										background: "#f8fafc",
										border: "1px solid #e2e8f0",
									}}>
									<HBox gap="10px" sx={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
										<VBox gap="3px" sx={{ flex: 1, minWidth: "220px" }}>
											<Typo variant="medium" sx={{ color: "#0f172a", fontWeight: 600 }}>
												{t("profile.protectionActive")}
											</Typo>
											<Typo variant="small" sx={{ color: "#64748b", lineHeight: "17px", fontSize: "12px" }}>
												{t("profile.protectionActiveHint")}
											</Typo>
										</VBox>
										<Button variant="secondary" title={t("profile.disable2fa")} onClick={handleDisableTwoFactor} />
									</HBox>
								</Box>
							) : (
								<>
									<SecurityMethodCard
										title={msg("emailOtp")}
										subtitle={t("profile.emailOtpSubtitle")}
										highlight={t("profile.emailOtpHighlight")}
										selectedLabel={t("profile.selected")}
										selected={selectedTwoFactorMethod === "Email OTP"}
										onClick={() => setSelectedTwoFactorMethod("Email OTP")}
									/>
									<SecurityMethodCard
										title="Google Authenticator"
										subtitle={t("profile.totpSubtitle")}
										highlight={t("profile.totpHighlight")}
										selectedLabel={t("profile.selected")}
										selected={selectedTwoFactorMethod === "Google Authenticator"}
										onClick={() => setSelectedTwoFactorMethod("Google Authenticator")}
									/>
								</>
							)}
						</HBox>
						{!isTwoFactorActive ? (
							<>
								<Spacer size="8px" />
								<Box
									sx={{
										padding: "11px 13px",
										borderRadius: "10px",
										background: "#f8fafc",
										border: "1px solid #e2e8f0",
									}}>
									<HBox gap="10px" sx={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
										<VBox gap="3px" sx={{ flex: 1, minWidth: "220px" }}>
											<Typo variant="medium" sx={{ color: "#0f172a", fontWeight: 600 }}>
												{displayText(selectedTwoFactorMethod)}
											</Typo>
											<Typo variant="small" sx={{ color: "#64748b", lineHeight: "17px", fontSize: "12px" }}>
												{twoFactorMethodDescription}
											</Typo>
										</VBox>
										<Button
											variant="primary"
											title={selectedTwoFactorMethod === "Google Authenticator" ? t("profile.continueSetup") : t("profile.enable2fa")}
											onClick={handleStartTwoFactorSetup}
										/>
									</HBox>
								</Box>
							</>
						) : null}

						{totpSetup && (
							<>
								<Spacer size="12px" />
								<VBox
									gap="12px"
									sx={{
										padding: "14px",
										borderRadius: "12px",
										background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)",
										border: "1px solid #dbeafe",
									}}>
									<VBox gap="4px">
										<Typo variant="medium" sx={{ color: "#0f172a", fontWeight: 600 }}>
											{t("profile.totpSetup")}
										</Typo>
										<Typo variant="small" sx={{ color: "#64748b", lineHeight: "17px", fontSize: "12px" }}>
											{t("profile.totpSetupHint")}
										</Typo>
									</VBox>
									<HBox gap="12px" sx={{ flexWrap: "wrap", alignItems: "stretch" }}>
										<Box
											sx={{
												flex: "0 0 188px",
												padding: "10px",
												borderRadius: "12px",
												background: "#ffffff",
												border: "1px solid #dbeafe",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
											}}>
											{totpQrCode ? <img src={totpQrCode} alt={msg("totpQrCode")} width="168" height="168" /> : null}
										</Box>
										<VBox gap="10px" sx={{ flex: "1 1 240px", minWidth: "220px" }}>
											<VBox
												gap="6px"
												sx={{
													padding: "10px 12px",
													borderRadius: "10px",
													background: "#ffffff",
													border: "1px solid #e2e8f0",
												}}>
												<Typo variant="small" sx={{ color: "#0f172a", fontWeight: 600, fontSize: "12px", lineHeight: "16px" }}>
													{t("profile.totpStep1")}
												</Typo>
												<Typo variant="small" sx={{ color: "#0f172a", fontWeight: 600, fontSize: "12px", lineHeight: "16px" }}>
													{t("profile.totpStep2")}
												</Typo>
												<Typo variant="small" sx={{ color: "#0f172a", fontWeight: 600, fontSize: "12px", lineHeight: "16px" }}>
													{t("profile.totpStep3")}
												</Typo>
											</VBox>
											<Input name="value" label={t("profile.authenticatorCode")} data={totpCode} onChange={v => setTotpCode({ value: v })} />
											<HBox gap="10px" sx={{ flexWrap: "wrap" }}>
												<Button variant="secondary" title={t("profile.copySetupLink")} onClick={handleCopyTotpUrl} />
												<Button variant="secondary" title={t("profile.chooseAnotherMethod")} onClick={resetTotpSetup} />
												<Button variant="primary" title={t("profile.confirmTotp")} onClick={handleConfirmTotp} />
											</HBox>
										</VBox>
									</HBox>
								</VBox>
							</>
						)}
					</SectionCard>

					<SectionCard title={t("profile.activeSessions")}>
						<HBox gap="10px" sx={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", marginBottom: "8px" }}>
							<VBox gap="3px" sx={{ flex: 1, minWidth: "220px" }}>
								<Typo variant="medium" sx={{ color: "#0f172a", fontWeight: 600 }}>
									{t("profile.authorizedDevices")}
								</Typo>
								<Typo variant="small" sx={{ color: "#64748b", lineHeight: "17px", fontSize: "12px" }}>
									{t("profile.authorizedDevicesHint")}
								</Typo>
							</VBox>
							<Button
								variant="secondary"
								title={otherSessionsCount > 0 ? t("profile.logoutOtherSessions", { count: otherSessionsCount }) : t("profile.noOtherSessions")}
								onClick={handleLogoutOtherSessions}
								disabled={sessionsBusy || otherSessionsCount === 0}
							/>
						</HBox>

						{sessionsBusy ? (
							<Typo variant="small" sx={{ color: "#64748b" }}>
								{t("profile.loadingSessions")}
							</Typo>
						) : sessions.length === 0 ? (
							<Typo variant="small" sx={{ color: "#64748b" }}>
								{t("profile.noSessions")}
							</Typo>
						) : (
							<VBox>
								{sessions.map((session, index) => (
									<VBox
										key={`${session.ip}-${session.created_at}-${index}`}
										sx={{
											padding: "12px 0",
											borderBottom: index === sessions.length - 1 ? "none" : "1px solid #f0f0f0",
											gap: "8px",
										}}>
										<HBox gap="8px" sx={{ justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap" }}>
											<VBox gap="4px" sx={{ flex: 1, minWidth: "240px" }}>
												<Typo variant="medium" sx={{ color: "#0f172a", fontWeight: 600 }}>
													{session.browser} • {session.os} • {session.device}
												</Typo>
												<Typo variant="small" sx={{ color: "#64748b", fontSize: "12px", lineHeight: "17px" }}>
													IP {session.ip}
												</Typo>
											</VBox>
											<HBox gap="6px" sx={{ flexWrap: "wrap" }}>
												{session.current ? <Badge label={t("profile.current")} /> : null}
												<Badge label={session.status} color={getSessionStatusColor(session.status, session.current)} />
											</HBox>
										</HBox>
										<HBox gap="14px" sx={{ flexWrap: "wrap" }}>
											<Typo variant="small" sx={{ color: "#64748b", fontSize: "12px" }}>
												{t("profile.created")}: <DateTimeTooltip value={session.created_at}>{formatDate(session.created_at, locale)}</DateTimeTooltip>
											</Typo>
											<Typo variant="small" sx={{ color: "#64748b", fontSize: "12px" }}>
												{t("profile.expires")}: <DateTimeTooltip value={session.expires_at}>{formatDate(session.expires_at, locale)}</DateTimeTooltip>
											</Typo>
										</HBox>
									</VBox>
								))}
							</VBox>
						)}
					</SectionCard>

					{/* Dates */}
					<SectionCard title={t("profile.activity")}>
						<InfoRow label={t("profile.created")} value={<DateTimeTooltip value={user.created_at}>{formatDate(user.created_at, locale)}</DateTimeTooltip>} />
						<InfoRow label={t("profile.updated")} value={<DateTimeTooltip value={user.updated_at}>{formatDate(user.updated_at, locale)}</DateTimeTooltip>} />
					</SectionCard>

					{/* Projects */}
					<SectionCard title={t("profile.projects")}>
						{user.projects.map(p => (
							<HBox
								key={p.id}
								sx={{
									padding: "10px 0",
									borderBottom: "1px solid #f0f0f0",
									alignItems: "center",
									gap: "8px",
									"&:last-child": { borderBottom: "none" },
								}}>
								<Box
									sx={{
										width: "8px",
										height: "8px",
										borderRadius: "50%",
										backgroundColor: p.id === project.id ? "#4285F4" : "#d1d5db",
										flexShrink: 0,
									}}
								/>
								<Typo variant="medium" sx={{ flex: 1 }}>
									{p.name}
								</Typo>
								<HBox gap="4px" sx={{ flexWrap: "wrap" }}>
									{p.roles.map(r => (
										<Badge
											key={r.name ?? r.id ?? r.role_id}
											label={r.name ?? getRoleName(r) ?? t("profile.role", { id: r.id ?? r.role_id ?? "unknown" })}
											variant="outline"
										/>
									))}
								</HBox>
							</HBox>
						))}
					</SectionCard>
				</VBox>
			) : (
				<VBox gap="12px">
					<Input name="value" label={t("profile.name")} data={name} onChange={v => setName({ value: v })} />

					<Spacer size="8px" />
					<Typo variant="small" sx={{ color: "#6b7280" }}>
						{t("profile.changePassword")}
					</Typo>

					<Input name="value" type="password" label={t("profile.currentPassword")} data={currentPassword} onChange={v => setCurrentPassword({ value: v })} />

					<Input name="value" type="password" label={t("profile.newPassword")} data={newPassword} onChange={v => setNewPassword({ value: v })} />

					<Input name="value" type="password" label={t("profile.confirmPassword")} data={confirmPassword} onChange={v => setConfirmPassword({ value: v })} />
				</VBox>
			)}

			<Spacer size="20px" />

			<HBox gap="12px" sx={{ width: "100%" }}>
				<Spacer grow />
				{!isEditing ? (
					<>
						<Button variant="secondary" title={t("common.close")} onClick={closePopup} />
						{/* <Button variant="primary" title="Edit" onClick={() => setIsEditing(true)} /> */}
					</>
				) : (
					<>
						<Button variant="secondary" title={t("profile.cancel")} onClick={() => setIsEditing(false)} />
						<Button variant="primary" title={t("profile.save")} onClick={handleSave} />
					</>
				)}
			</HBox>
		</Panel>
	);
};
